import { useEffect, useState } from 'react';
import { Users, ChevronDown, ChevronUp, User, Calendar, CheckCircle, XCircle, Eye } from 'lucide-react';
import ApplicationDetailContent from './ApplicationDetailContent';
import StartChatButton from './StartChatButton';

interface Applicant {
  id: string;
  status: 'PENDING' | 'WITHDRAWN' | 'REVIEWED' | 'ACCEPTED' | 'REJECTED';
  created_at: string;
  updated_at: string;
  user_id: string;
  resume_snapshot: any;
}

interface Props {
  jobPostingId: string;
}

const STATUS_LABELS: Record<string, { text: string; color: string }> = {
  PENDING: { text: '대기 중', color: 'bg-blue-100 text-blue-700' },
  REVIEWED: { text: '열람됨', color: 'bg-indigo-100 text-indigo-700' },
  ACCEPTED: { text: '합격', color: 'bg-green-100 text-green-700' },
  REJECTED: { text: '불합격', color: 'bg-red-100 text-red-700' },
};

export default function JobApplicantsList({ jobPostingId }: Props) {
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    fetchApplicants();
  }, [jobPostingId]);

  const fetchApplicants = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/provider/jobs/${jobPostingId}/applicants`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('지원자 목록을 불러오지 못했습니다.');
      }
      const data = await response.json();
      setApplicants(data.applicants || []);
    } catch (err) {
      console.error('Failed to fetch applicants:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: 'REVIEWED' | 'ACCEPTED' | 'REJECTED') => {
    setUpdatingId(id);
    try {
      const response = await fetch(`/api/v1/provider/applications/${id}/status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '상태 변경에 실패했습니다.');
      }
      const data = await response.json();
      setApplicants(prev => prev.map(a => (a.id === id ? { ...a, ...data.application } : a)));
    } catch (err: any) {
      alert(err.message || '상태 변경에 실패했습니다.');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });

  if (loading) {
    return (
      <div className="text-sm text-gray-500 py-4 text-center">지원자 목록 로딩 중...</div>
    );
  }

  if (applicants.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-lg border border-dashed border-gray-200">
        아직 지원자가 없습니다.
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-gray-900">지원자 {applicants.length}명</span>
      </div>
      <div className="space-y-2">
        {applicants.map(applicant => {
          const isExpanded = expandedId === applicant.id;
          const statusInfo = STATUS_LABELS[applicant.status];
          const name = applicant.resume_snapshot?.user_name || applicant.resume_snapshot?.user_email || '지원자';

          return (
            <div key={applicant.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div
                className="p-3 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedId(isExpanded ? null : applicant.id)}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {applicant.resume_snapshot?.photo_url ? (
                      <img src={applicant.resume_snapshot.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">{name}</span>
                        {statusInfo && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color} shrink-0`}>
                            {statusInfo.text}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <Calendar className="w-3 h-3" />
                        {formatDate(applicant.created_at)} 지원
                      </div>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-100 p-4 bg-gray-50/50">
                  <ApplicationDetailContent snapshot={applicant.resume_snapshot} />

                  {/* 상태 변경 + 메시지 버튼 */}
                  <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2">
                    <button
                      onClick={() => updateStatus(applicant.id, 'REVIEWED')}
                      disabled={updatingId === applicant.id || applicant.status === 'REVIEWED'}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      열람 표시
                    </button>
                    <button
                      onClick={() => updateStatus(applicant.id, 'ACCEPTED')}
                      disabled={updatingId === applicant.id || applicant.status === 'ACCEPTED'}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      합격
                    </button>
                    <button
                      onClick={() => updateStatus(applicant.id, 'REJECTED')}
                      disabled={updatingId === applicant.id || applicant.status === 'REJECTED'}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      불합격
                    </button>
                    <StartChatButton
                      targetUserId={applicant.user_id}
                      jobPostingId={jobPostingId}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
